import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { tenantService, Tenant, User, TenantStats, CreateUserData, UpdateUserData, UpdateTenantData } from '../services/tenantService';
import { subscriptionService, Subscription } from '../services/subscriptionService';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import { ChartBarIcon, UsersIcon, CreditCardIcon, CogIcon } from '../components/Icons';
import SubscriptionCard from '../components/SubscriptionCard';
import LeadPreferenceForm from '../components/LeadPreferenceForm';
import UpgradeModal from '../components/UpgradeModal';
import LiteUpgradeCta from '../components/essentials/LiteUpgradeCta';
import { ListPageHeader } from '../components/crm/ListPageHeader';

const TenantManagement: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const billingOnlyLock = params.get('locked') === '1';
  const { user } = useAuth();
  const { refetch: refetchEntitlements } = useEntitlements();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'settings' | 'billing'>(() => {
    const tab = params.get('tab');
    if (tab === 'billing' || billingOnlyLock) return 'billing';
    if (tab === 'settings') return 'settings';
    if (tab === 'users') return 'users';
    return 'overview';
  });
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    loadData();
  }, [location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (billingOnlyLock) {
        const subscriptionData = await subscriptionService.getSubscription().catch(() => null);
        setSubscription(subscriptionData);
        setTenant(null);
        setUsers([]);
        setStats(null);
        return;
      }
      const [tenantData, usersData, statsData, subscriptionData] = await Promise.all([
        tenantService.getTenant(),
        tenantService.getUsers(),
        tenantService.getTenantStats(),
        subscriptionService.getSubscription().catch(() => null) // Handle case where subscription might not exist
      ]);
      setTenant(tenantData);
      // Ensure users is always an array
      setUsers(Array.isArray(usersData) ? usersData : []);
      setStats(statsData);
      setSubscription(subscriptionData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant data');
      // Set empty array on error to prevent map errors
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      const newUser = await tenantService.createUser(userData);
      setUsers(prevUsers => [...(Array.isArray(prevUsers) ? prevUsers : []), newUser]);
      setShowAddUserModal(false);
      await loadData(); // Refresh stats
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userId: string, userData: UpdateUserData) => {
    try {
      const updatedUser = await tenantService.updateUser(userId, userData);
      setUsers(prevUsers => 
        Array.isArray(prevUsers) 
          ? prevUsers.map(u => u.id === userId ? updatedUser : u)
          : []
      );
      setShowEditUserModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await tenantService.deleteUser(userId);
        setUsers(prevUsers => 
          Array.isArray(prevUsers) 
            ? prevUsers.filter(u => u.id !== userId)
            : []
        );
        await loadData(); // Refresh stats
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete user');
      }
    }
  };

  const handleUpgradeSubscription = async (planCode: string) => {
    try {
      const response = await subscriptionService.upgradeSubscription(planCode);
      if ('checkout_url' in response && response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }
      if ('subscription' in response && response.subscription) setSubscription(response.subscription);
      setShowUpgradeModal(false);
      await refetchEntitlements();
      alert('Plan updated successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upgrade subscription');
    }
  };

  const handleUpdateTenant = async (tenantData: UpdateTenantData) => {
    try {
      const updatedTenant = await tenantService.updateTenant(tenantData);
      setTenant(updatedTenant);
      setShowEditTenantModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'sales': return 'bg-blue-100 text-blue-800';
      case 'employee': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading tenant management...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ListPageHeader
        title="Settings & Administration"
        subtitle="Manage organization settings, users, and billing — business profile details live here as the source of truth."
      />

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {billingOnlyLock && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="text-sm text-amber-800">
            Account access is restricted until billing is resolved. You can still manage your subscription here.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-surface rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-3 sm:p-6">
        <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto whitespace-nowrap -mx-1 px-1">
          {[
            ...(!billingOnlyLock ? [{ id: 'overview', name: 'Overview', icon: <ChartBarIcon size={5} /> }] : []),
            ...(!billingOnlyLock ? [{ id: 'users', name: 'Users', icon: <UsersIcon size={5} /> }] : []),
            { id: 'billing', name: 'Billing', icon: <CreditCardIcon size={5} /> },
            ...(!billingOnlyLock ? [{ id: 'settings', name: 'Settings', icon: <CogIcon size={5} /> }] : [])
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`shrink-0 py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200 flex items-center ${
                activeTab === tab.id
                  ? 'bg-gray-100 dark:bg-gray-700 text-fg shadow-sm'
                  : 'text-fg-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-surface overflow-hidden shadow rounded-lg border border-border">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-fg-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-fg-muted truncate">Total Users</dt>
                      <dd className="text-lg font-medium text-fg">{stats.users.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface overflow-hidden shadow rounded-lg border border-border">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-fg-muted truncate">Active Users</dt>
                      <dd className="text-lg font-medium text-fg">{stats.users.active}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface overflow-hidden shadow rounded-lg border border-border">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-fg-muted truncate">Recent Users</dt>
                      <dd className="text-lg font-medium text-fg">{stats.users.recent}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Role Distribution */}
          <div className="bg-surface shadow rounded-lg border border-border">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-fg mb-4">User Role Distribution</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.users.role_distribution.map((role) => (
                  <div key={role.role} className="text-center">
                    <div className="text-2xl font-bold text-fg">{role.count}</div>
                    <div className="text-sm text-fg-muted capitalize">{role.role}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          {subscription ? (
            <>
              <SubscriptionCard
                subscription={subscription}
                onUpgrade={() => setShowUpgradeModal(true)}
              />
              <LiteUpgradeCta dismissible={false} />
            </>
          ) : (
            <div className="bg-surface rounded-2xl shadow-lg border border-border p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCardIcon size={6} className="text-fg-muted" />
                </div>
                <h3 className="text-lg font-semibold text-fg mb-2">No Subscription Found</h3>
                <p className="text-fg-secondary mb-4">
                  It looks like you don't have a subscription set up yet.
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="bg-primary text-white px-6 py-2 rounded-xl font-medium hover:bg-primary-dark transition-all duration-200"
                >
                  Choose a Plan
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">Users</h2>
            {user?.role === 'admin' && (
              <button
                onClick={() => setShowAddUserModal(true)}
                className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Add User
              </button>
            )}
          </div>

          <div className="bg-surface shadow overflow-hidden sm:rounded-md border border-border">
            <ul className="divide-y divide-border">
              {Array.isArray(users) && users.map((userItem) => (
                <li key={userItem.id}>
                  <div className="px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {userItem.first_name[0]}{userItem.last_name[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-fg">
                          {userItem.first_name} {userItem.last_name}
                        </div>
                        <div className="text-sm text-fg-muted">{userItem.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(userItem.role)} dark:opacity-80`}>
                        {userItem.role}
                      </span>
                      {user?.role === 'admin' && userItem.id !== user.id && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(userItem);
                              setShowEditUserModal(true);
                            }}
                            className="text-primary hover:text-primary-dark text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(userItem.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && tenant && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">Tenant Settings</h2>
            {user?.role === 'admin' && (
              <button
                onClick={() => setShowEditTenantModal(true)}
                className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Edit Settings
              </button>
            )}
          </div>

          <div className="bg-surface shadow overflow-hidden sm:rounded-lg border border-border mb-6">
            <div className="px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-fg-muted">Organization Name</dt>
                  <dd className="mt-1 text-sm text-fg">{tenant.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-fg-muted">Domain</dt>
                  <dd className="mt-1 text-sm text-fg">{tenant.domain || 'Not set'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-fg-muted">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      tenant.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}>
                      {tenant.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-fg-muted">Created</dt>
                  <dd className="mt-1 text-sm text-fg">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {user?.role === 'admin' ? (
            <div className="bg-surface shadow overflow-hidden sm:rounded-lg border border-border">
              <div className="px-4 py-5 sm:p-6">
                <LeadPreferenceForm />
              </div>
            </div>
          ) : (
            <div className="bg-surface shadow overflow-hidden sm:rounded-lg border border-border p-4 text-sm text-fg-muted">
              Lead suggestion preferences can only be edited by organization admins.
            </div>
          )}
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onSuccess={handleCreateUser}
        />
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditUserModal(false);
            setSelectedUser(null);
          }}
          onSuccess={(data) => handleUpdateUser(selectedUser.id, data)}
        />
      )}

      {/* Edit Tenant Modal */}
      {showEditTenantModal && tenant && (
        <EditTenantModal
          tenant={tenant}
          onClose={() => setShowEditTenantModal(false)}
          onSuccess={handleUpdateTenant}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgradeSubscription}
        currentPlanCode={subscription?.plan_code || null}
      />
    </div>
  );
};

// Add User Modal Component
interface AddUserModalProps {
  onClose: () => void;
  onSuccess: (data: CreateUserData) => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    phone: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      onSuccess(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border border-border w-full max-w-md shadow-lg rounded-md bg-surface">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-fg mb-4">Add New User</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg-secondary">Email *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-fg-secondary">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-secondary">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="employee">Employee</option>
                <option value="sales">Sales</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-secondary">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-secondary">Password *</label>
              <input
                type="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-fg-secondary bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit User Modal Component
interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSuccess: (data: UpdateUserData) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<UpdateUserData>({
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role,
    phone: user.phone || '',
    is_active: user.is_active,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      onSuccess(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border border-border w-full max-w-md shadow-lg rounded-md bg-surface">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-fg mb-4">Edit User</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg-secondary">Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-gray-50 dark:bg-gray-700 text-fg-muted"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-fg-secondary">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-fg-secondary">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-secondary">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="employee">Employee</option>
                <option value="sales">Sales</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-secondary">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-primary focus:ring-primary border-border rounded bg-input"
              />
              <label className="ml-2 block text-sm text-fg">Active</label>
            </div>

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-fg-secondary bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Tenant Modal Component
interface EditTenantModalProps {
  tenant: Tenant;
  onClose: () => void;
  onSuccess: (data: UpdateTenantData) => void;
}

const EditTenantModal: React.FC<EditTenantModalProps> = ({ tenant, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<UpdateTenantData>({
    name: tenant.name,
    domain: tenant.domain || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      onSuccess(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border border-border w-full max-w-md shadow-lg rounded-md bg-surface">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-fg mb-4">Edit Tenant Settings</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg-secondary">Organization Name *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-secondary">Domain</label>
              <input
                type="text"
                name="domain"
                value={formData.domain}
                onChange={handleChange}
                placeholder="example.com"
                className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-input text-fg focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-fg-secondary bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TenantManagement;
